// A drop-in replacement for an <Outlet/> that makes the routes under it swipe-navigable: a gesture-driven,
// fully interruptible slide, and the SAME slide for ordinary Link/navigate() moves between adjacent routes.
// It renders the live route inside a positioned "stage" and drives snapshot + live-preview layers over it
// imperatively (see transition-controller). Everything animation-related is client-only and created in
// effects, so the server render and hydration are byte-identical to a bare <Outlet/>.
//
// COMPOSING IT ONTO A NEW SURFACE
//   1. List the routes it owns, IN ORDER, and pass them as `group`. A swipe steps one member; a link to any
//      member slides straight there in one screen.
//   2. Render <GestureTransitionOutlet group={...} /> where that route's <Outlet/> was.
//   3. Move the routes' padding onto `contentClassName` and let the stage (`className`) span the full width
//      they occupy. Padding left outside the stage does not travel with the page — see `contentClassName`.
//   4. If the surface is NESTED inside another one, pass `previewFrom` = the path of the route you're mounted
//      in (e.g. '/admin'), and have that route render ONLY its <Outlet/> when `useTransitionPreviewFrom()`
//      returns that path. See the note on `previewFrom` below for why.
//
// Surfaces nest freely: the root outlet swipes /request ⇄ /admin, and a second one inside the admin console
// swipes its tabs. A press is arbitrated per DIRECTION, innermost first (see use-edge-swipe), so on the first
// admin tab a right-swipe isn't claimed by the tabs and falls through to pop you back to /request.

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { Outlet, useRouter, useRouterState } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { TransitionController } from './transition-controller'
import { useEdgeSwipe } from './use-edge-swipe'
import { RoutePreview, useTransitionPreviewFrom } from './route-preview'
import { captureSnapshot } from './snapshot'
import { navVisualPath } from '../core/nav-visual-path'
import { rtlog, contentTag, rtInstallDump } from '../core/debug'
import { rememberSnapshot, recallSnapshot } from './snapshot-cache'
import { findLinkTransition, swipeTargets, type RoutePath, type TransitionOutletProps } from './surface.config'
import { deferRouteCommit } from '../core/defer-commit'
import { transitionsSuppressed } from '../core/motion'

// useLayoutEffect on the server warns; the link slide is client-only so fall back to useEffect during SSR.
const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

export function GestureTransitionOutlet(props: TransitionOutletProps) {
  const router = useRouter()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const liveRef = useRef<HTMLDivElement | null>(null)
  const controllerRef = useRef<TransitionController | null>(null)
  // The destination being previewed live inside the incoming pane of a drag, or null when no drag owns the
  // surface. Mounting this renders the real destination route (loaders, chunk, streaming) — see below.
  const previewRef = useRef<HTMLDivElement | null>(null)
  const [previewTo, setPreviewTo] = useState<RoutePath | null>(null)
  // Destinations whose pointer-down preloadRoute has RESOLVED (chunk + loader data in cache). Read only to flag,
  // in dev, a commit that had to do that work synchronously — the sync render `deferRouteCommit` exists to move
  // off the settle frame. See the `navigate` callback and the `preload` handler below.
  const preloadDoneRef = useRef<Set<string>>(new Set())

  const { group } = props
  const scope = props.previewFrom ?? '/'
  // Inside a live preview we ARE the pane's content: the shadow router renders this tree to be looked at, not
  // gestured on. Stay a plain outlet — no controller, no layers, no second preview router underneath.
  const inPreview = useTransitionPreviewFrom() !== null

  // Only intercept touch scrolling on routes that actually have a transition; elsewhere the surface behaves
  // exactly as before. `pan-y` lets vertical scroll through while reserving horizontal for the gesture.
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const armed = !inPreview && swipeTargets(group, pathname).length > 0
  // NOT `location.pathname`. The router moves that at the START of a navigation, while the <Outlet/> is still
  // rendering the OLD matches — teardown keyed off it dropped the panes to reveal the page we had just left.
  // `resolvedLocation` is the one whose matches have actually rendered, which is the only thing teardown can
  // safely uncover.
  const livePath = useRouterState({ select: (s) => s.resolvedLocation?.pathname ?? s.location.pathname })

  // The pane that slides in — a LIVE render of the destination via a shadow router (the whole design lives
  // in route-preview.tsx). A link click uses exactly the same pane, which is what lets its slide start
  // before the router has rendered anything.
  //
  // flushSync because the caller needs the pane element inside THIS event — React would otherwise not commit
  // the node until after the frame, and the slide would start against nothing. The synchronous render is
  // cheap: it paints the destination's pendingComponent, and the real content streams in behind it.
  const incomingFor = (to: RoutePath) => {
    flushSync(() => setPreviewTo(to))
    const layer = previewRef.current
    if (layer) return { layer, applyScroll: () => {}, managed: true }
    // No pane, no slide — deliberately no hand-rolled fallback skeleton: the preview already renders the
    // destination's own pendingComponent, and a generic shimmer just swapped a fake page shape for the
    // real one. The preview mounts synchronously above, so this is a can't-happen guard, not a fallback.
    rtlog('preview-unavailable', { scope, to })
    return null
  }
  // Mirrored so the controller-lifecycle effect below need not list it. That effect DISPOSES the controller on
  // cleanup, so re-running it because a closure was re-created would tear the engine down mid-gesture.
  // `incomingFor`'s only reactive input is `scope`, which that effect already lists, so a ref loses nothing.
  const incomingForRef = useRef(incomingFor)
  useLayoutEffect(() => {
    incomingForRef.current = incomingFor
  })

  useEffect(() => {
    const root = rootRef.current
    const live = liveRef.current
    if (!root || !live || inPreview) return
    // The gesture only navigates when it COMPLETES (the URL never changes mid-drag, and a cancel is a no-op —
    // nothing to revert). The target's chunk + data are preloaded on pointer-down, so this paints immediately.
    const controller = new TransitionController(
      root,
      live,
      {
        // The gesture-commit navigation (and a link-slide dragged back, and a chain's background commit) all
        // land here. Defer it past the current frame so the destination's synchronous React render commits
        // under the covering pane a task later, instead of blocking the frame the spring settles on (see
        // defer-commit.ts).
        navigate: (to) => {
          if (import.meta.env.DEV) rtlog('commit', { to, preloaded: preloadDoneRef.current.has(to) })
          return deferRouteCommit(() => router.navigate({ to }))
        },
        // The COMMITTED location (moves at the start of a navigation) — what finish() checks so a stale
        // gesture commit never stomps a link the user clicked mid-settle.
        getLocation: () => router.state.location.pathname,
        releasePreview: () => setPreviewTo(null),
      },
      props.kind,
      props.interactiveIncoming,
    )
    controller.setLivePath(router.state.resolvedLocation?.pathname ?? router.state.location.pathname)
    controllerRef.current = controller
    const uninstallDump = rtInstallDump(root, scope) // window.__rtDump() — run it when the UI wedges

    // Auto-flag a wedged/blank state so the rolling log captures it even without a manual __rtDump(): a short
    // moment after a navigation resolves, if the controller is idle there must be no leftover snapshot and the
    // live pane must have content.
    const checkAnomaly = () => {
      window.setTimeout(() => {
        const c = controllerRef.current
        if (!c || c.getState() !== 'idle' || !rootRef.current || !liveRef.current) return
        const strays = rootRef.current.querySelectorAll(':scope > [data-rt-layer]').length
        const liveTag = contentTag(liveRef.current)
        // Recorded in the rolling buffer either way (visible via `__rtDump()`); no console noise.
        if (strays > 0 || liveTag === 'BLANK') {
          rtlog('ANOMALY', { scope, url: router.state.location.pathname, strays, liveTag })
        }
      }, 700)
    }

    // A Link/navigate()/back-forward between two adjacent routes should slide too. `onBeforeLoad` fires
    // before the destination's loaders run and before the old route unmounts — reliably for both push and
    // pop, including navigations that follow a redirect (unlike onBeforeNavigate). Three cases:
    //   • a programmatic slide is already animating → just re-aim its spring so a rapid back/forward reverses
    //     smoothly from wherever it is (retarget), no re-snapshot, no jump.
    //   • the controller is idle → snapshot the outgoing view now (DOM still mounted) and stash it; the
    //     layout effect below snapshots the destination and starts the slide once it has committed.
    //   • a gesture owns the controller (its own commit fires this too) → ignore.
    const unsub = router.subscribe('onBeforeLoad', (e) => {
      if (transitionsSuppressed()) return
      const from = e.fromLocation?.pathname
      const to = e.toLocation?.pathname
      if (!from || !to) return
      const transition = findLinkTransition(group, from, to)
      if (!transition) return
      const outgoingTag = contentTag(live)
      rtlog('nav', { scope, from, to, st: controller.getState(), outgoing: outgoingTag })

      // The gesture's own commit / cancel-revert navigations surface here too — they're already being animated
      // by the controller. Never treat them as a fresh link nav (doing so force-reset the cancel mid-flight and
      // flashed the destination — the cancel flicker).
      if (controller.isInternalNav()) {
        rtlog('skip-internal-nav', { scope, from, to })
        return
      }

      if (controller.canRetarget()) {
        // A navigation landed mid-slide: let the controller pick re-aim vs clean re-arm. The departing
        // snapshot (the page being left, fully rendered right now) is a factory — the controller only pays
        // its deep clone on the redirect branch, never on a plain retarget/reverse.
        controller.interruptNav(
          transition,
          from,
          () => captureSnapshot(live),
          () => incomingForRef.current(transition.to),
        )
        return
      }
      // A navigation arriving while a previous transition is wedged (e.g. a gesture-cancel revert that got
      // superseded and never settled) would otherwise be hidden behind its leftover snapshot — force it clean
      // so this navigation is visible, then fall through to animate it fresh.
      if (controller.getState() === 'finishing') controller.forceReset()
      if (controller.getState() !== 'idle') return
      // Capture the outgoing view. In rapid back-and-forth the page we're leaving may still be re-suspending
      // (blank) at this instant — fall back to the last snapshot of it we took while it WAS fully rendered,
      // so the outgoing pane shows real content instead of an empty box. Only on the BLANK path:
      // recallSnapshot deep-clones its cached layer, and this line runs on the frame that must paint the
      // slide's first movement, so an unconditional recall would cost a full-page clone per nav.
      const fresh = captureSnapshot(live)
      let outgoing = fresh
      if (outgoingTag === 'BLANK') {
        const cached = recallSnapshot(scope, from)
        rtlog('outgoing-blank-fallback', { scope, from, used: cached ? 'cache' : 'blank' })
        if (cached) outgoing = cached
      } else {
        // Bank this render as the last-good copy of `from`, for exactly the BLANK case above. The banking
        // clone itself runs off this frame (see rememberSnapshot) — nothing here but a reference hand-off.
        rememberSnapshot(scope, from, fresh.layer)
      }
      // Pin the highlight to the origin from the instant navigation starts, so it doesn't briefly jump to the
      // destination before the slide (and its midpoint flip) begins.
      navVisualPath.set(from)
      // Arm the panes RIGHT HERE, synchronously, before returning to the router.
      //
      // This has to happen inside `onBeforeLoad` and nowhere later. The router is about to load the
      // destination and commit it into the live layer, and if the outgoing snapshot isn't already covering by
      // the time React paints that, you SEE the destination land uncovered and only then get a slide. A layout
      // effect keyed on the preview mounting is a beat too late — React has already painted. `incomingFor`
      // flushSync's the preview pane into place so it exists on this very line.
      const incoming = incomingForRef.current(transition.to)
      if (!incoming) return
      controller.animate(transition, from, outgoing, incoming)
    })

    // Safety net: after every navigation settles, purge anything a wedged transition might have left behind
    // (a covering snapshot / a transformed live layer), and flag it if something looks off.
    const unsubResolved = router.subscribe('onResolved', () => {
      controller.reconcile()
      rtlog('resolved', { scope, to: router.state.location.pathname, st: controller.getState() })
      checkAnomaly()
    })

    return () => {
      unsub()
      unsubResolved()
      uninstallDump()
      controller.dispose()
      controllerRef.current = null
    }
  }, [router, group, scope, inPreview, props.kind, props.interactiveIncoming])

  // Tell the controller what the live <Outlet/> now holds. Teardown waits on this: the panes may only be
  // dropped once the layer they are about to reveal actually shows the route the slide landed on. Waiting on
  // the COMMIT rather than on a navigation promise is what lets the animation start before the router has
  // rendered anything at all.
  useIsoLayoutEffect(() => {
    controllerRef.current?.setLivePath(livePath)
  }, [livePath])

  useEdgeSwipe({
    rootRef,
    controllerRef,
    enabled: !inPreview,
    group,
    edgeOnly: props.edgeOnly,
    interactiveIncoming: props.interactiveIncoming,
    getPathname: () => router.state.location.pathname,
    preload: (to) => {
      // Fired on pointer-down, before the drag is even locked in: pull the destination's route chunk AND its
      // loader data into cache while the finger moves, so committing it on release paints without a beat.
      // Guard hard: preloadRoute can throw synchronously (a router-internal `_nonReactive` error), and a throw
      // here would abort the pointer-down handler that starts the gesture.
      try {
        rtlog('preload-start', { to })
        void Promise.resolve(router.preloadRoute({ to }))
          .then(() => {
            preloadDoneRef.current.add(to)
            rtlog('preload-ok', { to })
          })
          .catch((err: unknown) => rtlog('preload-fail', { to, err: String(err).slice(0, 120) }))
      } catch (err) {
        rtlog('preload-throw', { to, err: String(err).slice(0, 120) })
      }
    },
    incomingFor,
  })

  return (
    <div
      ref={rootRef}
      // Names this surface — '/' for the root one, the host route for a nested one. Read by the debug dump and
      // by the e2e specs, which need to address one stage when two are on screen.
      data-rt-surface={scope}
      className={cn('relative isolate flex flex-1 flex-col', props.className)}
      style={armed ? { touchAction: 'pan-y' } : undefined}
    >
      <div ref={liveRef} className={cn('flex min-h-0 flex-1 flex-col', props.contentClassName)}>
        <Outlet />
      </div>
      {previewTo ? (
        // The live destination pane of a drag. Positioned and transformed by the controller (which treats it
        // as a `managed` pane: styled like any layer, but never appended or removed — React owns the node).
        //
        // `inert` is the pane's RESTING state, and the value here never changes: it keeps a route that isn't
        // committed yet out of the tab order and the a11y tree, and stops any stray pointer event from reaching
        // a page the user hasn't actually navigated to. On a surface that opts into `interactiveIncoming` the
        // controller LIFTS it imperatively for the phases where the page may be touched (see applyPaneFlags) and
        // restores it before handing the node back — imperatively, and not from React state, because re-rendering
        // this tree mid-gesture is exactly what the engine exists to avoid. React never rewrites the attribute:
        // the prop it reconciles against is this constant.
        <div
          key={previewTo}
          ref={previewRef}
          data-rt-layer="preview"
          data-rt-to={previewTo}
          className={cn(
            'absolute inset-0 flex min-h-0 flex-col overflow-hidden [will-change:transform] [contain:layout_paint]',
            props.contentClassName,
          )}
          inert
        >
          {/* Rendered URGENTLY, inside the same flushSync that mounts this host, so the pane holds the real
              destination from the FIRST frame of the slide. Never at deferred priority: it saves nothing and
              the pane slides in EMPTY. The chunk and data are already cached by the time we get here. */}
          <RoutePreview to={previewTo} previewFrom={scope} />
        </div>
      ) : null}
    </div>
  )
}
